/**
 * Planned stops and live arrivals as a single vertical timeline.
 *
 * The workspace used to list waypoints; the Live tab listed the same stops
 * with ETAs. Two copies of the same line, one of them stale. This is both:
 * arrived, the current transit, the next stop, and what is still ahead.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Mission,
  MissionDeviceState,
  MissionProgress,
  MissionRouteEvent,
  MissionTimelineStop,
} from '../../services/missionApi';
import { BLUE, GOLD, GREEN, MUTED } from './missionLiveParts';
import { activeRouteLabel } from '../routePlanner/io/missionRoute';
import { buildRouteTimeline, formatRecalcAge, type RouteStopView } from './routeTimeline';

export interface MissionRouteWidgetProps {
  mission: Mission;
  timeline?: MissionTimelineStop[];
  progress?: MissionProgress | null;
  devices?: MissionDeviceState[];
  events?: MissionRouteEvent[];
  units?: 'km' | 'mi';
  onFocus?: (longitude: number, latitude: number) => void;
}

const MissionRouteWidget: React.FC<MissionRouteWidgetProps> = ({
  mission,
  timeline,
  progress,
  devices,
  events,
  units = 'mi',
  onFocus,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 10000);
    return () => window.clearInterval(id);
  }, []);

  const view = useMemo(
    () => buildRouteTimeline({ mission, timeline, progress, devices, events, nowMs, units }),
    [mission, timeline, progress, devices, events, nowMs, units],
  );

  const activeName = activeRouteLabel(mission);
  const parked = (mission.saved_routes || []).filter(
    (route) => route.id !== (mission.active_route_id || 'primary'),
  );
  const header = [
    view.km,
    view.stopCount > 0
      ? `${view.stopCount} waypoint${view.stopCount === 1 ? '' : 's'}`
      : null,
    view.profile,
  ].filter(Boolean).join(' · ');

  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.1,
        py: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.15 }}>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, letterSpacing: 0.2 }}>
          {activeName}
        </Typography>
        <Typography sx={{
          fontSize: '0.55rem',
          fontWeight: 800,
          letterSpacing: 0.5,
          color: BLUE,
        }}
        >
          ACTIVE
        </Typography>
        <Typography sx={{ ml: 'auto', fontSize: '0.62rem', color: MUTED }}>
          {formatRecalcAge(view.recalcAt, nowMs) || ''}
        </Typography>
      </Box>
      <Typography sx={{ display: 'block', mb: parked.length ? 0.35 : 1, fontSize: '0.68rem', color: MUTED }}>
        {view.stops.length === 0
          ? 'No stops yet. Edit the mission to plan the route.'
          : header}
      </Typography>
      {parked.length > 0 && (
        <Typography sx={{ display: 'block', mb: 1, fontSize: '0.62rem', color: MUTED }}>
          Also {parked.map((route) => route.label).filter(Boolean).join(' · ')}
        </Typography>
      )}

      {view.stops.map((stop, index) => (
        <React.Fragment key={stop.id}>
          {view.transit && index === view.nextIndex && view.nextIndex >= 0 && (
            <TransitCard transit={view.transit} />
          )}
          <StopRow stop={stop} last={index === view.stops.length - 1} onFocus={onFocus} />
        </React.Fragment>
      ))}
    </Box>
  );
};

const TransitCard: React.FC<{ transit: NonNullable<ReturnType<typeof buildRouteTimeline>['transit']> }> = ({
  transit,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      mb: 0.85,
      ml: 0.15,
    }}
  >
    <Box sx={{ width: 16, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: 0.85 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 4, bgcolor: BLUE }} />
    </Box>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1,
        py: 0.7,
        borderRadius: 1.2,
        bgcolor: 'rgba(0,0,0,0.28)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>
          In transit · leg {transit.leg} of {transit.of}
        </Typography>
        {transit.stopped && (
          <Typography sx={{
            ml: 'auto',
            fontSize: '0.64rem',
            fontWeight: 700,
            color: GOLD,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
          }}
          >
            <Box component="span" sx={{
              width: 7,
              height: 8,
              display: 'inline-block',
              borderLeft: `2px solid ${GOLD}`,
              borderRight: `2px solid ${GOLD}`,
            }}
            />
            stopped
          </Typography>
        )}
      </Box>
      {(transit.total > 0 || transit.spread) && (
        <Typography sx={{ display: 'block', mt: 0.15, fontSize: '0.62rem', color: MUTED }}>
          {[
            transit.total > 0 ? `${transit.live} of ${transit.total} reporting` : null,
            transit.spread ? `spread ${transit.spread}` : null,
          ].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Box>
  </Box>
);

const StopRow: React.FC<{
  stop: RouteStopView;
  last: boolean;
  onFocus?: (longitude: number, latitude: number) => void;
}> = ({ stop, last, onFocus }) => {
  const [copied, setCopied] = useState(false);
  const focusable = stop.latitude != null && stop.longitude != null && !!onFocus;
  const tone = stop.phase === 'reached' ? GREEN : stop.phase === 'next' ? BLUE : MUTED;

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!stop.coords) return;
    try {
      await navigator.clipboard.writeText(stop.coords);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, pb: last ? 0 : 1.05 }}>
      <Box sx={{ width: 16, flexShrink: 0, position: 'relative' }}>
        {!last && (
          <Box sx={{
            position: 'absolute',
            left: 7,
            top: 16,
            bottom: -18,
            width: 2,
            bgcolor: stop.phase === 'reached' ? 'rgba(87,201,107,0.45)' : 'rgba(255,255,255,0.1)',
          }}
          />
        )}
        <Node phase={stop.phase} />
      </Box>
      <Box
        role={focusable ? 'button' : undefined}
        tabIndex={focusable ? 0 : undefined}
        onClick={() => {
          if (focusable) onFocus?.(stop.longitude!, stop.latitude!);
        }}
        onKeyDown={(e) => {
          if (!focusable) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onFocus?.(stop.longitude!, stop.latitude!);
          }
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: focusable ? 'pointer' : 'default',
          borderRadius: 0.5,
          '&:hover': focusable ? { bgcolor: 'rgba(255,255,255,0.03)' } : undefined,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.78rem',
            fontWeight: stop.phase === 'next' ? 800 : 700,
          }}
          >
            {stop.label}
          </Typography>
          {stop.clock && (
            stop.phase === 'next' && stop.late ? (
              <Box sx={{
                px: 0.7,
                py: 0.1,
                borderRadius: 1,
                bgcolor: 'rgba(240,180,41,0.18)',
                color: GOLD,
                fontSize: '0.64rem',
                fontWeight: 800,
              }}
              >
                {stop.clock}
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.68rem', color: MUTED, flexShrink: 0 }}>
                {stop.clock}
              </Typography>
            )
          )}
        </Box>
        <Typography sx={{ display: 'block', mt: 0.1, fontSize: '0.62rem', color: MUTED }}>
          {stop.phase === 'next' && stop.late && stop.plannedClock && (
            <Box component="span" sx={{ textDecoration: 'line-through', mr: 0.5 }}>
              {stop.plannedClock}
            </Box>
          )}
          {stop.phase === 'next' && stop.late && stop.plannedClock && (
            <Box component="span" sx={{ mr: 0.45, color: tone }}>→ </Box>
          )}
          {stop.detail}
        </Typography>
        {stop.phase === 'next' && stop.coords && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.2 }}>
            <Typography sx={{ fontSize: '0.62rem', color: MUTED, fontFamily: 'ui-monospace, monospace' }}>
              {stop.coords}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={copy}
              aria-label="Copy coordinates"
              sx={{
                border: 0,
                p: 0,
                m: 0,
                bgcolor: 'transparent',
                color: copied ? GREEN : MUTED,
                cursor: 'pointer',
                display: 'inline-flex',
                lineHeight: 0,
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 11 }} />
            </Box>
          </Box>
        )}
        {stop.note && (
          <Typography sx={{ display: 'block', mt: 0.15, fontSize: '0.62rem', color: MUTED, whiteSpace: 'pre-wrap' }}>
            {stop.note}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const Node: React.FC<{ phase: RouteStopView['phase'] }> = ({ phase }) => {
  if (phase === 'reached') {
    return (
      <Box sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        bgcolor: GREEN,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1,
      }}
      >
        <Box sx={{
          width: 6,
          height: 3.5,
          borderLeft: '1.6px solid #0b1a10',
          borderBottom: '1.6px solid #0b1a10',
          transform: 'rotate(-45deg)',
          mb: '2px',
        }}
        />
      </Box>
    );
  }
  return (
    <Box sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      border: `2px solid ${phase === 'next' ? BLUE : 'rgba(255,255,255,0.22)'}`,
      bgcolor: '#14181d',
      position: 'relative',
      zIndex: 1,
    }}
    />
  );
};

export default MissionRouteWidget;
